import { useEffect, useState } from 'react';
import { Table, Typography } from 'antd';
import { message } from '../../utils/antd-message';
import dayjs from 'dayjs';
import {
  entitlementBalancesApi,
  type EntitlementBalanceHistoryItem,
} from '../../api/entitlementBalances.api';
import {
  lessonKindLabel,
  ledgerBusinessRefLabel,
  ledgerTypeLabel,
} from './LedgerDetailDrawer';
import { DraggableModal } from '../../components/DraggableModal';
import { useGlobalCampusScope } from '../../auth/GlobalCampusContext';

const { Text } = Typography;

/** 弹窗上下文：学生 + SKU（三列数字入口共用同一数据源）。 */
export interface BalanceHistoryContext {
  studentId: string;
  studentName: string;
  skuId: string;
  skuName: string;
}

function formatDateTime(value: string): string {
  return dayjs(value).format('YYYY-MM-DD HH:mm');
}

/**
 * 课时流水明细弹窗（ADM-34，entitlement-balance-inquiry）。
 *
 * 点击行内购买剩余/赠送剩余/可用剩余任一课时数字打开：展示该学生该 SKU
 * 的全类型流水（H-3 单行双池，lessonKind 标明变动所属池），时间倒序分页；冲正行附业务原因。
 * 只读视图，无任何修改入口。
 */
export function BalanceHistoryModal(props: {
  context: BalanceHistoryContext | null;
  onClose: () => void;
}) {
  const { context, onClose } = props;
  const [rows, setRows] = useState<EntitlementBalanceHistoryItem[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(false);
  const pageSize = 10;
  // 全局校区范围：'ALL' 不携带 campusId（锁定模式，与列表口径一致）（O-27）
  const { scope } = useGlobalCampusScope();

  useEffect(() => {
    if (!context) {
      setRows([]);
      setTotal(0);
      setPage(1);
      return;
    }
    void (async () => {
      setLoading(true);
      try {
        const res = await entitlementBalancesApi.listHistory({
          campusId: scope === 'ALL' ? undefined : scope,
          studentId: context.studentId,
          skuId: context.skuId,
          page,
          pageSize,
        });
        setRows(res.items);
        setTotal(res.total);
      } catch {
        message.error('加载课时流水失败');
      } finally {
        setLoading(false);
      }
    })();
  }, [context, page, scope]);

  return (
    <DraggableModal
      open={context !== null}
      title={
        context
          ? `课时流水 - ${context.studentName} · ${context.skuName}`
          : '课时流水'
      }
      footer={null}
      width={880}
      onCancel={onClose}
      destroyOnHidden
    >
      <Text type="secondary">
        以下为该学生该课程 SKU 全部账户的课时流水（时间倒序）；课时余额由流水汇总生成，不提供修改入口。
      </Text>
      <Table<EntitlementBalanceHistoryItem>
        style={{ marginTop: 12 }}
        size="small"
        rowKey="id"
        loading={loading}
        columns={[
          { title: '时间', dataIndex: 'createdAt', width: 130, render: (v) => formatDateTime(v) },
          {
            title: '课时类别',
            dataIndex: 'lessonKind',
            width: 80,
            render: (v) => lessonKindLabel[v as keyof typeof lessonKindLabel] ?? v,
          },
          {
            title: '类型',
            dataIndex: 'type',
            width: 90,
            render: (v) => ledgerTypeLabel[v] ?? v,
          },
          {
            title: '变动数量',
            dataIndex: 'availableChange',
            width: 90,
            render: (v: number) => (v > 0 ? `+${v}` : `${v}`),
          },
          { title: '变动后可用', dataIndex: 'availableAfter', width: 90 },
          {
            title: '业务来源',
            dataIndex: 'businessRefType',
            width: 150,
            render: (v, row) => (
              <>
                {ledgerBusinessRefLabel[v as keyof typeof ledgerBusinessRefLabel] ?? v}
                <Text type="secondary" style={{ marginLeft: 4 }}>
                  {row.businessRefId}
                </Text>
              </>
            ),
          },
          { title: '操作人', dataIndex: 'operatorName', width: 90, render: (v) => v ?? '-' },
          {
            title: '冲正原因',
            dataIndex: 'reason',
            render: (v: string | null, row) =>
              row.type === 'REVERSAL' ? (v ?? '-') : null,
          },
        ]}
        dataSource={rows}
        pagination={{
          current: page,
          pageSize,
          total,
          showSizeChanger: false,
          onChange: (target) => setPage(target),
        }}
      />
    </DraggableModal>
  );
}
